import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { EditAccountTablePagination } from "../../../components/AccountDetailPolicy/editAccountTableControls";
import { scorePercent, scopeLabel } from "../AuditHub/utils";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
  searchFieldSx,
} from "../AuditHub/theme";
import { FAILED_TEXT_COLOR, formatCount, labelSx, PASSED_COLOR, FAILED_COLOR, PASSED_TEXT_COLOR, ReviewTrack, Tile } from "./AuditCardParts";
import { readProgress } from "./auditMetrics";
import { accountAvailability } from "./AuditContextBar";
const LINK_COLOR = "#1976d2";

const PREVIEW_LIMIT = 3;
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const SORTS = [
  { value: "lowest", label: "Lowest score first" },
  { value: "highest", label: "Highest score first" },
  { value: "name", label: "Account name (A–Z)" },
];

const byName = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base", numeric: true });

// Unscored accounts have nothing to rank, so they always follow the scored ones.
const byScore = (direction) => (a, b) => {
  const aMissing = a.percent == null;
  const bMissing = b.percent == null;
  if (aMissing || bMissing) return Number(aMissing) - Number(bMissing) || byName(a, b);
  return (a.percent - b.percent) * direction || byName(a, b);
};

const COMPARATORS = { lowest: byScore(1), highest: byScore(-1), name: byName };

const closeXSx = {
  p: 0,
  m: 0,
  border: "none",
  background: "transparent",
  color: AH_WHITE,
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  WebkitTextStroke: `0.6px ${AH_WHITE}`,
};

const greyButtonSx = {
  textTransform: "none",
  fontSize: "0.875rem",
  fontWeight: 600,
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  boxShadow: "none",
  color: AH_WHITE,
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  "&:hover": { backgroundColor: "#5a6268", borderColor: "#5a6268", boxShadow: "none" },
};

const linkButtonSx = {
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 700,
  color: LINK_COLOR,
  minWidth: 0,
  px: 0.75,
  py: 0,
  flexShrink: 0,
  "&:hover": { backgroundColor: "#e3f2fd" },
};

const ScoreBar = ({ percent }) => (
  <Box sx={{ height: 6, borderRadius: 5, overflow: "hidden", display: "flex", bgcolor: FAILED_COLOR }}>
    <Box sx={{ width: `${percent}%`, height: "100%", bgcolor: PASSED_COLOR }} />
  </Box>
);

const fractionTip = (entry) =>
  entry.reviewTotal > 0
    ? `${formatCount(entry.decided)} decided · ${formatCount(entry.inReview)} in review · ${formatCount(entry.outstanding)} outstanding`
    : "No manual controls";

const ScorePercent = ({ percent, emptyTip }) => {
  const text = (
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: AH_TEXT, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
      {percent == null ? "—" : `${Math.round(percent)}%`}
    </Typography>
  );
  if (percent == null && emptyTip) {
    return (
      <Tooltip title={emptyTip} placement="top" disableInteractive>
        {text}
      </Tooltip>
    );
  }
  return text;
};

const ScoreRow = ({ entry, isCurrent, compact, reviewMode }) => {
  const label = (
    <Typography
      sx={{
        fontSize: compact ? 12 : 13,
        fontWeight: 500,
        color: isCurrent || !compact ? AH_TEXT : AH_MUTED,
        minWidth: 0,
        ...(compact
          ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
          : { overflowWrap: "anywhere", lineHeight: 1.35 }),
      }}
    >
      {entry.label}
      {isCurrent && !compact ? (
        <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 700, color: LINK_COLOR }}>
          Viewing
        </Box>
      ) : null}
    </Typography>
  );

  const name = compact ? (
    <Tooltip title={entry.label} placement="top" disableInteractive>
      {label}
    </Tooltip>
  ) : (
    label
  );

  if (!reviewMode) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: compact
            ? "minmax(0, 1.2fr) minmax(60px, 2fr) 52px"
            : { xs: "minmax(0, 1fr) 72px 48px", sm: "minmax(0, 1fr) 180px 52px" },
          alignItems: "center",
          gap: compact ? 1 : 1.5,
          width: "100%",
          textAlign: "left",
        }}
      >
        {name}
        {entry.percent == null ? (
          <Typography sx={{ gridColumn: "2 / span 2", fontSize: 12, color: AH_MUTED }}>{entry.note}</Typography>
        ) : (
          <>
            <ScoreBar percent={entry.percent} />
            <ScorePercent percent={entry.percent} emptyTip={entry.note} />
          </>
        )}
      </Box>
    );
  }

  const awaitingScore = entry.percent == null && entry.reviewTotal <= 0;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%", minWidth: 0, textAlign: "left" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.2fr) minmax(72px, 1.6fr) max-content 44px",
          alignItems: "center",
          gap: compact ? 1 : 1.5,
          width: "100%",
        }}
      >
        {name}
        {awaitingScore ? (
          <Typography sx={{ gridColumn: "2 / span 3", fontSize: 12, color: AH_MUTED }}>{entry.note}</Typography>
        ) : entry.reviewTotal > 0 ? (
          <>
            <ReviewTrack
              decided={entry.decided}
              inReview={entry.inReview}
              outstanding={entry.outstanding}
              total={entry.reviewTotal}
              height={6}
            />
            <Tooltip title={fractionTip(entry)} placement="top" disableInteractive>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: AH_TEXT, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                {formatCount(entry.decided)}/{formatCount(entry.reviewTotal)}
              </Typography>
            </Tooltip>
            <ScorePercent percent={entry.percent} emptyTip={entry.note} />
          </>
        ) : (
          <>
            <Typography sx={{ gridColumn: "2 / span 2", fontSize: 12, color: AH_MUTED }}>No manual controls</Typography>
            <ScorePercent percent={entry.percent} emptyTip={entry.note} />
          </>
        )}
      </Box>
      {!compact && entry.reviewTotal > 0 ? (
        <Typography sx={{ fontSize: 12, color: AH_MUTED }}>{fractionTip(entry)}</Typography>
      ) : null}
    </Box>
  );
};

const AccountScoresDialog = ({ open, onClose, entries, compliance, accountNumber, onSelect, reviewMode }) => {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("lowest");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSort("lowest");
      setPage(0);
    }
  }, [open]);

  const sorted = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = needle ? entries.filter((entry) => entry.search.includes(needle)) : entries.slice();
    return list.sort(COMPARATORS[sort]);
  }, [entries, query, sort]);

  const scoredCount = useMemo(() => entries.filter((entry) => entry.percent != null).length, [entries]);
  const lastPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);
  const pageRows = sorted.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="audit-account-scores-title"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "none",
          border: `1px solid ${AH_INPUT_BORDER}`,
          height: { sm: "min(680px, calc(100% - 64px))" },
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id="audit-account-scores-title"
            sx={{ fontWeight: 700, fontSize: { xs: "1.125rem", sm: "1.25rem" }, color: AH_WHITE, letterSpacing: "-0.02em", lineHeight: 1.2 }}
          >
            {reviewMode ? "Manual Controls" : "Account Scores"}
          </Typography>
          <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem", overflowWrap: "anywhere" }}>
            {compliance} · {scoredCount} of {entries.length} accounts scored
          </Typography>
        </Box>
        <Box component="button" type="button" aria-label="Close" onClick={onClose} sx={closeXSx}>
          <span aria-hidden="true">×</span>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          p: { xs: 1.5, sm: 2.5 },
          backgroundColor: AH_TRACK,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <TextField
            size="small"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
            }}
            placeholder="Search by account name or number"
            inputProps={{ "aria-label": "Search accounts" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: AH_MUTED }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...searchFieldSx, flex: "1 1 220px", minWidth: 0 }}
          />
          <TextField
            select
            size="small"
            value={sort}
            onChange={(event) => {
              setSort(event.target.value);
              setPage(0);
            }}
            inputProps={{ "aria-label": "Sort accounts" }}
            sx={{ ...searchFieldSx, flex: { xs: "1 1 100%", sm: "0 0 200px" } }}
          >
            {SORTS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.875rem" }}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: { xs: 240, sm: 0 },
            display: "flex",
            flexDirection: "column",
            backgroundColor: AH_WHITE,
            border: `1px solid ${AH_INPUT_BORDER}`,
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {pageRows.length ? (
              pageRows.map((entry) => {
                const isCurrent = entry.accountNumber === accountNumber;
                const canOpen = (!entry.reason || entry.pendingScan) && !isCurrent;
                return (
                  <Tooltip key={entry.key} title={entry.reason} placement="top" disableInteractive>
                    <Box component="span" sx={{ display: "block" }}>
                      <ButtonBase
                        onClick={() => onSelect(entry.accountNumber)}
                        disabled={!canOpen}
                        sx={{
                          display: "flex",
                          width: "100%",
                          px: 2,
                          py: 1.1,
                          borderBottom: `1px solid ${AH_BORDER}`,
                          backgroundColor: isCurrent ? "#eff6ff" : "transparent",
                          "&:hover": { backgroundColor: "#f8fafc" },
                          "&.Mui-focusVisible": { backgroundColor: "#e3f2fd" },
                          "&.Mui-disabled": { opacity: isCurrent ? 1 : 0.7 },
                        }}
                      >
                        <ScoreRow entry={entry} isCurrent={isCurrent} reviewMode={reviewMode} />
                      </ButtonBase>
                    </Box>
                  </Tooltip>
                );
              })
            ) : (
              <Typography sx={{ p: 3, textAlign: "center", fontSize: "0.875rem", color: AH_MUTED, overflowWrap: "anywhere" }}>
                No accounts match “{query.trim()}”.
              </Typography>
            )}
          </Box>
          {sorted.length > ROWS_PER_PAGE_OPTIONS[0] ? (
            <EditAccountTablePagination
              page={currentPage}
              rowsPerPage={rowsPerPage}
              total={sorted.length}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onPageChange={setPage}
              onRowsPerPageChange={(value) => {
                setRowsPerPage(value);
                setPage(0);
              }}
            />
          ) : null}
        </Box>
      </Box>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${AH_INPUT_BORDER}`, bgcolor: AH_WHITE }}>
        <Button type="button" variant="contained" disableElevation onClick={onClose} sx={greyButtonSx}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Per-account scores for one framework. Account View lists every account and scrolls when the
 * rows exceed the card; beyond PREVIEW_LIMIT the full list also opens in a searchable dialog.
 */
const summaryRowSx = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  columnGap: 1,
  alignItems: "baseline",
  "& > *": { minWidth: 0 },
};

const summaryLabelSx = {
  fontSize: 13,
  fontWeight: 600,
  textAlign: "center",
  lineHeight: 1.3,
};

const summaryValueSx = {
  fontSize: 16,
  fontWeight: 700,
  color: AH_TEXT,
  textAlign: "center",
  lineHeight: 1.2,
  fontVariantNumeric: "tabular-nums",
};

const ManualSummary = ({ total, passed, failed, notApplicable }) => (
  <Box sx={{ flexShrink: 0, minWidth: 0 }}>
    <Box sx={summaryRowSx}>
      <Typography noWrap sx={{ ...summaryLabelSx, color: AH_MUTED }}>Total</Typography>
      <Typography noWrap sx={{ ...summaryLabelSx, color: PASSED_TEXT_COLOR }}>Passed</Typography>
      <Typography noWrap sx={{ ...summaryLabelSx, color: FAILED_TEXT_COLOR }}>Failed</Typography>
      <Typography noWrap sx={{ ...summaryLabelSx, color: AH_MUTED }}>N/A</Typography>
    </Box>
    <Box sx={summaryRowSx}>
      {[
        ["total", total],
        ["passed", passed],
        ["failed", failed],
        ["notApplicable", notApplicable],
      ].map(([key, value]) => (
        <Typography key={key} noWrap sx={summaryValueSx}>
          {formatCount(value)}
        </Typography>
      ))}
    </Box>
  </Box>
);

const AuditAccountScores = ({ accounts, compliance, accountNumber, onAccountChange, manual, sx }) => {
  const [open, setOpen] = useState(false);

  const entries = useMemo(
    () =>
      (Array.isArray(accounts) ? accounts : []).map((account) => {
        const { scope, reason, pendingScan } = accountAvailability(account, compliance);
        const progress = readProgress(scope?.progress);
        return {
          key: account.auditAccountId || account.accountNumber,
          accountNumber: account.accountNumber,
          label: scopeLabel(account),
          search: `${account.accountName || ""} ${account.accountNumber || ""}`.toLowerCase(),
          percent: scope?.hasScanResult ? scorePercent(scope.auditScore) : null,
          decided: progress.decided,
          inReview: progress.inReview,
          outstanding: progress.outstanding,
          reviewTotal: progress.total,
          note: scope ? "Pending automated scan completion" : "Framework not assessed",
          reason,
          pendingScan,
        };
      }),
    [accounts, compliance]
  );

  const overflow = entries.length > PREVIEW_LIMIT;
  const showReview = entries.some((entry) => entry.reviewTotal > 0);

  const select = (nextAccountNumber) => {
    setOpen(false);
    if (onAccountChange) onAccountChange(nextAccountNumber);
  };

  return (
    <Tile
      label="Manual Controls"
      align="start"
      sx={{ ...sx, minHeight: 0, overflow: "hidden" }}
    >
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <ManualSummary
          total={Number(manual?.total) || 0}
          passed={Number(manual?.passed) || 0}
          failed={Number(manual?.failed) || 0}
          notApplicable={Number(manual?.notApplicable) || 0}
        />
        <Box sx={{ flexShrink: 0, mt: 1.5, mb: 1.5, borderTop: `1px solid ${AH_BORDER}` }} />
        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
          <Typography noWrap sx={labelSx}>Account View</Typography>
          {overflow ? (
            <Button size="small" onClick={() => setOpen(true)} sx={linkButtonSx}>
              View all
            </Button>
          ) : null}
        </Box>
        <Box
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: "min(280px, 40vh)",
            overflowY: "auto",
            overscrollBehavior: "contain",
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          {entries.map((entry) => (
            <ScoreRow
              key={entry.key}
              entry={entry}
              isCurrent={entry.accountNumber === accountNumber}
              compact
              reviewMode={showReview}
            />
          ))}
        </Box>
      </Box>
      {overflow ? (
        <AccountScoresDialog
          open={open}
          onClose={() => setOpen(false)}
          entries={entries}
          compliance={compliance}
          accountNumber={accountNumber}
          onSelect={select}
          reviewMode={showReview}
        />
      ) : null}
    </Tile>
  );
};

export default AuditAccountScores;
